import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { ToastTypes, withToast } from "../../contexts/toastr.context";
import { CommunityPac } from "../../types/community/community-pac.type";
import { Community } from "../../types/community/community.type";
import { User } from "../../types/user/minimal-user.type";
import { Loader } from "../../_components";
import { ActionModal } from "../../_components/action-modal.component";
import { Button } from "../../_components/button.component";
import { Checkbox } from "../../_components/form-controls/checkbox.component";
import IconMenu2 from "../../_components/icon-menu-2.component";
import PremiumFeature from "../../_components/premium/premium-feature.component";
import { history } from "../../_config";
import { EmbeddedSectionService } from "../../_service/embedded-section.service";
import { getCommunitySettingEmbeddedSectionEditPageRoute } from "./embedded-section-edit.page";
import { useAppService } from "../../hooks/use-app-service";

export const COMMUNITY_SETTING_EMBEDDED_SECTION_PAGE_ROUTE =
  "/settings/embedded-sections";

interface Props {
  user: User;
  community: Community;
  communityPac: CommunityPac;
  addToast?: any;
}

function CommunitySettingEmbeddedSectionsPageComponent({
  user,
  community,
  communityPac,
  addToast,
}: Props) {
  const communityId = community?.id ?? null;

  const { analyticsService } = useAppService();

  const [sections, setSections] = useState<any[]>([]);
  const [isSectionDataLoading, setIsSectionDataLoading] = useState(false);
  const [isSectionDataLoadingCompleted, setIsSectionDataLoadingCompleted] =
    useState(false);

  const [selectedSection, setSelectedSection] = useState<any>(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  // get all the sections
  useEffect(() => {
    if (communityId) {
      setIsSectionDataLoading(true);
      EmbeddedSectionService.getEmbeddedSections(communityId, true)
        .then(({ embeddedSections }) => {
          setSections(embeddedSections);
        })
        .catch((error) => {
          console.log({ error });
          addToast("Failed to get embedded channels", "", ToastTypes.danger);
        })
        .finally(() => {
          setIsSectionDataLoading(false);
          setIsSectionDataLoadingCompleted(true);
        });
    }
  }, [communityId]);

  const updateSectionState = (section: any) => {
    // find section index
    const sectionIndex = sections.findIndex((s) => s.id === section.id);

    if (sectionIndex > -1) {
      const newSections = [...sections];
      newSections[sectionIndex] = section;
      setSections(newSections);
    }
  };

  const deleteSection = () => {
    EmbeddedSectionService.deleteEmbeddedSection(
      community.id,
      selectedSection.id
    )
      .then(({ embeddedSection }) => {
        // remove section matching the id from sections
        const newSections = sections.filter((s) => s.id !== embeddedSection.id);
        setSections(newSections);
        setSelectedSection(null);
        setIsDeleteModalVisible(false);
        addToast("Embedded channel deleted successfully");
      })
      .catch((error) => {
        console.log({ error });
        addToast("Error deleting embedded channel", "", ToastTypes.danger);
      });
  };

  const sortSection = (direction: string, section: any) => {
    console.log(sections.map((s) => s.id));
    if (direction === "up") {
      const newSections = [...sections];
      const sectionIndex = newSections.findIndex((s) => s.id === section.id);
      if (sectionIndex > 0) {
        const temp = newSections[sectionIndex - 1];
        newSections[sectionIndex - 1] = newSections[sectionIndex];
        newSections[sectionIndex] = temp;
        // new order
        EmbeddedSectionService.updateEmbeddedSectionSortOrder(
          communityId,
          newSections.map((s) => s.id)
        )
          .then(() => {
            setSections(newSections);
          })
          .catch((error) => {
            console.log({ error });
            addToast(
              "Error updating embedded channel sort order",
              "",
              ToastTypes.danger
            );
          });
      } else {
        addToast("Cannot move up", "", ToastTypes.danger);
      }
    } else {
      const newSections = [...sections];
      const sectionIndex = newSections.findIndex((s) => s.id === section.id);
      if (sectionIndex < newSections.length - 1) {
        const temp = newSections[sectionIndex + 1];
        newSections[sectionIndex + 1] = newSections[sectionIndex];
        newSections[sectionIndex] = temp;
        // new order
        EmbeddedSectionService.updateEmbeddedSectionSortOrder(
          communityId,
          newSections.map((s) => s.id)
        )
          .then(() => {
            setSections(newSections);
          })
          .catch((error) => {
            console.log({ error });
            addToast(
              "Error updating embedded channel sort order",
              "",
              ToastTypes.danger
            );
          });
      } else {
        addToast("Cannot move down", "", ToastTypes.danger);
      }
    }
  };

  const deleteSectionTrigger = (section: any) => {
    setSelectedSection(section);
    setIsDeleteModalVisible(true);
  };

  if (!isSectionDataLoadingCompleted) {
    return (
      <div className="flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  return (
    <PremiumFeature isAvailable={communityPac.embedSection} hideFeature>
      <div className="Card rounded shadow theme-bg-surface">
        {/* headers */}
        <div className="pl-4 pr-2 py-4  border-b theme-border-default flex items-center justify-between">
          <span className="font-bold theme-text-heading-2">
            Embedded Channels
          </span>
          {/* create new key button */}
          <Button
            label="+ Create"
            className="mx-2"
            onClick={() => {
              analyticsService.track("create-new-embed-channel-button-clicked")
              history.push(
                getCommunitySettingEmbeddedSectionEditPageRoute(null)
              );
            }}
          />
        </div>
        <div className="card p-5 theme-text-heading-2 mt-2 pt-4">
          {sections.length === 0 ? (
            <div className="text-center">
              <h3 className="text-lg font-bold">No embedded channels found</h3>
              <p className="text-gray-600">
                You can add embedded channels by clicking the button above
              </p>
            </div>
          ) : (
            sections.map((section) => (
              <EmbeddedSectionTile
                key={section.id}
                section={section}
                user={user}
                community={community}
                updateSectionState={updateSectionState}
                deleteSection={deleteSectionTrigger}
                sortSection={sortSection}
              />
            ))
          )}
        </div>
        {/* delete modal */}
        <ActionModal
          active={isDeleteModalVisible}
          onSubmit={deleteSection}
          header="Delete Channel"
          setActive={setIsDeleteModalVisible}
          title="Are you sure you want to delete this channel?"
          btnColor="red"
          isLoading={undefined}
          children={undefined}
        />
      </div>
    </PremiumFeature>
  );
}

interface EmbeddedSectionTileProps {
  section: any;
  user: User;
  community: Community;
  updateSectionState: (section: any) => void;
  deleteSection: (section: any) => void;
  sortSection?: (direction: string, section: any) => void;
}
function EmbeddedSectionTile({
  section,
  user,
  community,
  updateSectionState,
  deleteSection,
  sortSection = (e) => {},
}: EmbeddedSectionTileProps) {
  const [isVisbilityChangeLoading, setIsVisbilityChangeLoading] =
    useState(false);

  const toggleSectionVisibility = (section: any) => {
    setIsVisbilityChangeLoading(true);
    EmbeddedSectionService.changeVisibilityEmbeddedSectionInCommunity(
      user,
      community,
      section
    )
      .then(({ embeddedSection }) => {
        updateSectionState(embeddedSection);
      })
      .catch((error) => {
        console.log({ error });
      })
      .finally(() => {
        setIsVisbilityChangeLoading(false);
      });
  };

  return (
    <div
      key={section.id}
      className="border rounded pl-4 py-1 pr-1 mb-3 flex justify-between items-center">
      <div
        className="font-semibold cursor-pointer"
        onClick={() => {
          history.push(
            getCommunitySettingEmbeddedSectionEditPageRoute(section.id)
          );
        }}>
        {section.name}
      </div>
      <div className="flex justify-center items-center">
        {/* visiblity check */}
        {isVisbilityChangeLoading ? (
          <Loader />
        ) : (
          <div
            className="flex cursor-pointer"
            onClick={() => {
              toggleSectionVisibility(section);
            }}>
            <div className="mr-2">Visiblity</div>
            <Checkbox selected={section.isActive} />
          </div>
        )}
        {/* actions */}
        <IconMenu2
          actions={[
            {
              // show share option
              icon: "edit",
              label: "Edit",
              onClick: () => {
                history.push(
                  getCommunitySettingEmbeddedSectionEditPageRoute(section.id)
                );
              },
            },
            {
              // show share option
              icon: "arrow-up",
              label: "Move Up",
              onClick: () => {
                sortSection("up", section);
              },
            },
            {
              // show share option
              icon: "arrow-down",
              label: "Move Down",
              onClick: () => {
                sortSection("down", section);
              },
            },
            {
              // show share option
              icon: "trash",
              label: "Delete",
              actionType: "alert",
              onClick: () => {
                deleteSection(section);
              },
            },
          ]}
        />
      </div>
    </div>
  );
}

const CommunitySettingEmbeddedSectionsPage = withToast(
  connect((s: any) => ({
    user: s.auth,
    community: s.community,
    communityPac: s.communityPac,
  }))(CommunitySettingEmbeddedSectionsPageComponent)
);

export default CommunitySettingEmbeddedSectionsPage;
